import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { components } from '@trackercop/contracts';
import { rootRoute } from './__root';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { PermissionGate } from '@/components/admin/permission-gate';
import { AdminSubNav } from '@/components/admin/admin-sub-nav';
import { EventTable } from '@/components/admin/event-table';

type EventLevel = components['schemas']['EventLevel'];
type EventSource = components['schemas']['EventSource'];
type Event = components['schemas']['Event'];

interface EventsSearch {
  level?: EventLevel;
  source?: EventSource;
}

const LEVELS: EventLevel[] = ['debug', 'info', 'warn', 'error'];
const SOURCES: EventSource[] = [
  'api',
  'cron',
  'queue:scan',
  'queue:alert',
  'orchestrator',
  'browser',
  'silent-killer',
];

export const adminObservabilityEventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/observability/events',
  component: AdminObservabilityEventsPage,
  validateSearch: (search: Record<string, unknown>): EventsSearch => ({
    level: LEVELS.includes(search.level as EventLevel)
      ? (search.level as EventLevel)
      : undefined,
    source: SOURCES.includes(search.source as EventSource)
      ? (search.source as EventSource)
      : undefined,
  }),
});

function AdminObservabilityEventsPage() {
  return (
    <PermissionGate permission="platform:observe">
      <Events />
    </PermissionGate>
  );
}

function Events() {
  const search = adminObservabilityEventsRoute.useSearch();
  const navigate = adminObservabilityEventsRoute.useNavigate();
  const [accumulated, setAccumulated] = useState<Event[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const filters = { level: search.level, source: search.source, cursor };
  const eventsQuery = useQuery({
    queryKey: queryKeys.admin.events(filters),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/admin/observability/events', {
        params: {
          query: {
            level: search.level,
            source: search.source,
            cursor,
            limit: 50,
          },
        },
      });
      if (error) throw error;
      return data;
    },
  });

  const filterKey = `${search.level ?? ''}|${search.source ?? ''}`;
  useEffect(() => {
    setAccumulated([]);
    setCursor(undefined);
  }, [filterKey]);

  const pageCursor = cursor ?? '__initial__';
  const pageItems = eventsQuery.data?.items;
  useEffect(() => {
    if (!pageItems) return;
    setAccumulated((prev) => [...prev, ...pageItems]);
    // Track which cursor we appended via a stable identity check —
    // dependencies cover both cursor and the page payload reference.
  }, [pageItems, pageCursor]);

  function setFilter(next: Partial<EventsSearch>) {
    navigate({
      search: (prev) => ({
        ...prev,
        ...next,
      }),
      replace: true,
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">Events</h1>
      <AdminSubNav active="events" />
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <label className="flex items-center gap-1">
          <span className="text-muted-foreground">Level</span>
          <select
            data-testid="events-level-filter"
            className="h-8 rounded-md border bg-background px-2"
            value={search.level ?? ''}
            onChange={(e) =>
              setFilter({ level: (e.target.value || undefined) as EventLevel | undefined })
            }
          >
            <option value="">all</option>
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1">
          <span className="text-muted-foreground">Source</span>
          <select
            data-testid="events-source-filter"
            className="h-8 rounded-md border bg-background px-2"
            value={search.source ?? ''}
            onChange={(e) =>
              setFilter({ source: (e.target.value || undefined) as EventSource | undefined })
            }
          >
            <option value="">all</option>
            {SOURCES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>
      {eventsQuery.isLoading && accumulated.length === 0 && (
        <p className="text-sm text-muted-foreground">Loading events…</p>
      )}
      {eventsQuery.isError && (
        <p className="text-sm text-red-600">Failed to load events.</p>
      )}
      <EventTable
        events={accumulated}
        hasMore={Boolean(eventsQuery.data?.nextCursor)}
        isLoadingMore={eventsQuery.isFetching && cursor !== undefined}
        onLoadMore={() => {
          const next = eventsQuery.data?.nextCursor ?? undefined;
          if (next) setCursor(next);
        }}
      />
    </div>
  );
}
